import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import {
  hasSalesOrderPdf,
  resolveSoPdfDownloadUrl
} from '../utils/soPdfDownload'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function buildCcEmails(userEmail, additionalInput, toEmail = '') {
  const result = []
  const seen = new Set()
  const toKey = String(toEmail || '').trim().toLowerCase()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (toKey && key === toKey) return
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

function pickOrderFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.salesOrders == null &&
    dataBlock.orders == null &&
    (dataBlock._id != null || dataBlock.cpoNumber != null || dataBlock.id != null)
  ) {
    return dataBlock
  }

  if (dataBlock.salesOrder && typeof dataBlock.salesOrder === 'object') {
    return dataBlock.salesOrder
  }
  if (dataBlock.order && typeof dataBlock.order === 'object') {
    return dataBlock.order
  }

  const list = dataBlock.salesOrders || dataBlock.orders
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((row) => String(row._id || row.id) === String(id))
  return found || list[0]
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return formatDateParts(d)
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function formatDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/)
  if (dateTimeMatch) {
    const [, dd, mm, yyyy, hh, min] = dateTimeMatch
    const hour = parseInt(hh, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${dd}/${mm}/${yyyy}, ${String(h12).padStart(2, '0')}:${min} ${period}`
  }

  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}`
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function formatStandaloneLabel(value) {
  if (value === true || value === 1 || value === '1') return 'Yes'
  if (value === false || value === 0 || value === '0') return 'No'

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
  if (normalized === 'yes' || normalized === 'true' || normalized === 'standalone') {
    return 'Yes'
  }
  if (
    normalized === 'no' ||
    normalized === 'false' ||
    normalized === 'linked' ||
    normalized === 'non-standalone'
  ) {
    return 'No'
  }
  return value == null || value === '' ? '-' : String(value)
}

function getMaterialGstPercent(row) {
  if (row?.gstPercent != null && row.gstPercent !== '') return row.gstPercent
  if (row?.gst != null && row.gst !== '') return row.gst
  return null
}

function formatMaterialMoney(value) {
  if (value == null || value === '') return '-'
  const n = parseFloat(value)
  return Number.isFinite(n) ? n.toFixed(2) : String(value)
}

function getMaterialBasicPrice(row) {
  if (row?.cpoBasicPrice != null && row.cpoBasicPrice !== '') return row.cpoBasicPrice
  if (row?.basicPrice != null && row.basicPrice !== '') return row.basicPrice
  return null
}

function getMaterialGstValueDisplay(row) {
  if (row?.gstValue != null && row.gstValue !== '') {
    return formatMaterialMoney(row.gstValue)
  }
  const basic = parseFloat(getMaterialBasicPrice(row)) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0 && gst > 0) {
    return ((basic * qty) * (gst / 100)).toFixed(2)
  }
  return '-'
}

function getMaterialTotalValueDisplay(row) {
  if (row?.totalValue != null && row.totalValue !== '') {
    return formatMaterialMoney(row.totalValue)
  }
  const basic = parseFloat(getMaterialBasicPrice(row)) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0) {
    const totalBasic = basic * qty
    const gstAmount = totalBasic * (gst / 100)
    return (totalBasic + gstAmount).toFixed(2)
  }
  return '-'
}

function calcGrossTotalsFromMaterials(materialRows) {
  return materialRows.reduce(
    (acc, row) => {
      const lineBasic =
        (parseFloat(getMaterialBasicPrice(row)) || 0) * (parseFloat(row?.quantity) || 0)
      let gstValue = parseFloat(row?.gstValue)
      if (!Number.isFinite(gstValue)) {
        const gst = parseFloat(getMaterialGstPercent(row)) || 0
        gstValue = (lineBasic * gst) / 100
      }
      let totalValue = parseFloat(row?.totalValue)
      if (!Number.isFinite(totalValue)) {
        totalValue = lineBasic + gstValue
      }
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function getGrossTotalsFromSource(source) {
  const grossTotalBasicPrice = Number(source?.grossTotalBasicPrice)
  const grossGstValue = Number(source?.grossGstValue)
  const grossTotalValue = Number(source?.grossTotalValue)
  if (
    Number.isFinite(grossTotalBasicPrice) &&
    Number.isFinite(grossGstValue) &&
    Number.isFinite(grossTotalValue)
  ) {
    return { grossTotalBasicPrice, grossGstValue, grossTotalValue }
  }
  return null
}

function getOrderGrossTotals(order, materialRows) {
  const fromOrder = getGrossTotalsFromSource(order)
  if (fromOrder) return fromOrder
  return calcGrossTotalsFromMaterials(materialRows)
}

function getOrderTermsRows(order) {
  if (Array.isArray(order?.terms) && order.terms.length > 0) {
    return order.terms.map((t) => ({
      term: t.term || t.rfqTerm || t.quoteTerm || '-',
      response: t.response || t.termsResponse || t.quoteResponse || '-'
    }))
  }
  return []
}

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function getAttachmentLabel(att) {
  if (!att) return 'Attachment'
  if (att.originalFileName) return att.originalFileName
  const key = att.s3Key || ''
  if (!key) return 'Attachment'
  const parts = String(key).split('/')
  return parts[parts.length - 1] || key
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || '').trim()
  const dotExt = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (dotExt && dotExt.length <= 6) return dotExt

  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'text/plain': 'txt',
    'application/zip': 'zip'
  }
  return typeMap[String(contentType || '').toLowerCase()] || ''
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(extension || getFileExtension('', contentType)).toLowerCase()

  if (ext === 'pdf') {
    return { icon: 'fa-file-pdf', color: '#dc3545', bg: '#fde8ea', label: 'PDF' }
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { icon: 'fa-file-excel', color: '#198754', bg: '#e8f5ee', label: ext.toUpperCase() || 'EXCEL' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { icon: 'fa-file-word', color: '#0d6efd', bg: '#e7f1ff', label: ext.toUpperCase() || 'WORD' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { icon: 'fa-file-image', color: '#6f42c1', bg: '#f3ebff', label: ext.toUpperCase() || 'IMAGE' }
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z') {
    return { icon: 'fa-file-zipper', color: '#fd7e14', bg: '#fff3e6', label: ext.toUpperCase() || 'ZIP' }
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return { icon: 'fa-file-powerpoint', color: '#d63384', bg: '#fce8f3', label: ext.toUpperCase() || 'PPT' }
  }
  if (ext === 'txt') {
    return { icon: 'fa-file-lines', color: '#6c757d', bg: '#f1f3f5', label: 'TXT' }
  }
  return {
    icon: 'fa-file',
    color: '#495057',
    bg: '#f8f9fa',
    label: ext ? ext.toUpperCase() : 'FILE'
  }
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)

const AttachmentTileGrid = ({ attachments, downloadingS3Key, onDownload, disabled }) => {
  if (!attachments?.length) return null

  return (
    <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att)
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType
        )
        const isDownloading = downloadingS3Key === att.s3Key
        return (
          <button
            key={att.s3Key}
            type="button"
            className="border-0 text-center"
            title={`Open ${fileName}`}
            onClick={() => onDownload(att.s3Key, att.url)}
            disabled={disabled || isDownloading}
            style={{
              width: '92px',
              padding: '10px 8px',
              borderRadius: '10px',
              backgroundColor: fileMeta.bg,
              border: `1px solid ${fileMeta.color}33`,
              cursor: disabled || isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: fileMeta.color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                marginBottom: '8px'
              }}
            >
              {isDownloading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className={`fa-solid ${fileMeta.icon}`} />
              )}
            </div>
            <div
              title={fileName}
              style={{
                fontSize: '11px',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                color: '#212529',
                width: '100%'
              }}
            >
              {fileName.length > 22 ? `${fileName.slice(0, 19)}...` : fileName}
            </div>
          </button>
        )
      })}
    </div>
  )
}

const SalesOrderView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpeningPdf, setIsOpeningPdf] = useState(false)
  const [downloadingS3Key, setDownloadingS3Key] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [ccUserSuggestions, setCcUserSuggestions] = useState([])
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
  const ccSearchDebounceRef = useRef(null)
  const ccSearchRequestIdRef = useRef(0)

  const ownerAttachments = useMemo(() => {
    if (!Array.isArray(order?.owner)) return []
    return order.owner.map(mapAttachmentFromApi).filter((att) => att.s3Key)
  }, [order?.owner])

  useEffect(() => {
    if (!id) {
      toast.error('Sales order ID is missing', { autoClose: 2000 })
      navigate('/salesOrder')
      return undefined
    }

    let cancelled = false

    const loadOrder = async () => {
      setIsLoading(true)
      try {
        const response = await axios.post(API_URL, {
          action: 'get-sales-order-by-id',
          id,
          currentUserId: localStorage.getItem('id') || undefined
        })
        if (cancelled) return

        const responseData = normalizeApiPayload(parseApiResponse(response.data))

        if (isApiSuccess(responseData)) {
          const row = pickOrderFromResponse(responseData, id)
          if (row) {
            setOrder(row)
          } else {
            toast.error('Sales order not found', { autoClose: 2000 })
            navigate('/salesOrder')
          }
        } else {
          toast.error(responseData?.message || 'Failed to fetch sales order details', {
            autoClose: 2000
          })
          navigate('/salesOrder')
        }
      } catch (error) {
        if (cancelled) return
        console.error('Fetch sales order error:', error)
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null
        toast.error(
          errData?.message || 'An error occurred while fetching sales order details',
          { autoClose: 2000 }
        )
        navigate('/salesOrder')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadOrder()

    return () => {
      cancelled = true
    }
  }, [id, navigate])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = localStorage.getItem('id')
      if (!userId) return
      try {
        const response = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        console.error('Fetch user email error:', error)
      }
    }
    fetchUserEmail()
  }, [])

  useEffect(() => {
    return () => {
      if (ccSearchDebounceRef.current) {
        clearTimeout(ccSearchDebounceRef.current)
      }
    }
  }, [])

  const getCcSearchTerm = (value) => {
    const segments = String(value || '').split(/[,;]/)
    return segments[segments.length - 1].trim()
  }

  const searchCcUsers = async (term, requestId) => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-users',
        page: 1,
        limit: 25,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: term,
        currentUserId: localStorage.getItem('id') || undefined
      })
      if (requestId !== ccSearchRequestIdRef.current) return

      const responseData = parseApiResponse(response.data)
      const users = Array.isArray(responseData?.users)
        ? responseData.users
        : Array.isArray(responseData?.data?.users)
          ? responseData.data.users
          : []

      const suggestions = []
      const seen = new Set()
      users.forEach((user) => {
        const email = String(user?.email || '').trim()
        if (!isValidEmailAddress(email)) return
        const key = email.toLowerCase()
        if (seen.has(key)) return
        seen.add(key)
        suggestions.push({
          name: String(user?.name || '').trim() || email,
          email
        })
      })
      setCcUserSuggestions(suggestions)
    } catch (error) {
      console.error('Search CC users error:', error)
      if (requestId === ccSearchRequestIdRef.current) {
        setCcUserSuggestions([])
      }
    } finally {
      if (requestId === ccSearchRequestIdRef.current) {
        setIsSearchingCcUsers(false)
      }
    }
  }

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value
    setAdditionalCcEmails(value)

    ccSearchRequestIdRef.current += 1
    const requestId = ccSearchRequestIdRef.current
    if (ccSearchDebounceRef.current) {
      clearTimeout(ccSearchDebounceRef.current)
    }

    const term = getCcSearchTerm(value)
    if (term.length < 3) {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
      return
    }

    setIsSearchingCcUsers(true)
    ccSearchDebounceRef.current = setTimeout(() => {
      searchCcUsers(term, requestId)
    }, 400)
  }

  const handleSelectCcUser = (email) => {
    const selected = String(email || '').trim()
    if (!selected) return

    setAdditionalCcEmails((prev) => {
      const value = String(prev || '')
      const lastComma = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
      const base = lastComma >= 0 ? `${value.slice(0, lastComma + 1)} ` : ''
      const existing = parseAdditionalCcEmails(base)
      if (existing.some((item) => item.toLowerCase() === selected.toLowerCase())) {
        return `${base.trim()} `.trimStart()
      }
      return `${base}${selected}, `
    })
    ccSearchRequestIdRef.current += 1
    setCcUserSuggestions([])
    setIsSearchingCcUsers(false)
  }

  const handleAdditionalCcBlur = () => {
    setTimeout(() => {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
    }, 180)
  }

  const handleViewPdf = async () => {
    if (!hasSalesOrderPdf(order)) {
      toast.error('PDF is not available for this sales order', { autoClose: 2000 })
      return
    }

    setIsOpeningPdf(true)
    try {
      const downloadUrl = await resolveSoPdfDownloadUrl({
        pdfDownloadUrl: order.pdfDownloadUrl,
        pdfLink: order.pdfLink,
        salesOrderId: id,
        currentUserId: localStorage.getItem('id') || undefined
      })
      setOrder((prev) =>
        prev && prev.pdfDownloadUrl === downloadUrl
          ? prev
          : { ...prev, pdfDownloadUrl: downloadUrl }
      )
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Open sales order PDF error:', error)
      toast.error(error.message || 'Failed to open PDF', { autoClose: 2000 })
    } finally {
      setIsOpeningPdf(false)
    }
  }

  const handleResendSalesOrderEmail = async () => {
    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }
    if (!id) {
      toast.error('Sales order ID is missing', { autoClose: 2000 })
      return
    }

    const toEmail = String(order?.customerMailId || '').trim()
    if (!toEmail) {
      toast.error('Customer email is missing', { autoClose: 2000 })
      return
    }
    if (!isValidEmailAddress(toEmail)) {
      toast.error('Customer email is invalid', { autoClose: 2000 })
      return
    }
    if (String(order?.status || '').toLowerCase() !== 'active') {
      toast.error('Sales order must be Active before resending email', {
        autoClose: 2000
      })
      return
    }
    if (!hasSalesOrderPdf(order)) {
      toast.error('PDF is not available for this sales order', { autoClose: 2000 })
      return
    }

    const ccList = buildCcEmails(userEmail, additionalCcEmails, toEmail)
    const invalidCc = ccList.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }

    const extraCcList = parseAdditionalCcEmails(additionalCcEmails).filter((email) => {
      const key = email.toLowerCase()
      return (
        key !== toEmail.toLowerCase() &&
        key !== String(userEmail || '').trim().toLowerCase()
      )
    })

    setIsSendingEmail(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'resend-sales-order-email',
        id,
        userid: userId,
        ccEmails: extraCcList
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || `Email resent to ${toEmail}`,
          { autoClose: 2000 }
        )
      } else {
        toast.error(responseData?.message || 'Failed to resend email', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Resend sales order email error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'An error occurred while resending email', {
        autoClose: 2000
      })
    } finally {
      setIsSendingEmail(false)
    }
  }

  const downloadOwnerAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return
    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-so-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        currentUserId: userId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const files = responseData.files || responseData.data?.files || []
      const downloadUrl =
        files[0]?.downloadUrl ||
        files[0]?.url ||
        files[0]?.presignedUrl ||
        responseData.downloadUrl ||
        fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Sales Order</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/salesOrder">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Sales order not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const materials = Array.isArray(order.materials) ? order.materials : []
  const materialGrossTotals = getOrderGrossTotals(order, materials)
  const termsRows = getOrderTermsRows(order)
  const cpoNo = order.cpoNumber || '-'
  const standaloneLabel = formatStandaloneLabel(order.standalone)

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="CPO Details" />

                    <ViewField label="CPO Number">{cpoNo}</ViewField>
                    <ViewField label="CPO Date">{formatDate(order.cpoDate)}</ViewField>
                    <ViewField label="Delivery Date">{formatDate(order.deliverDate || order.deliveryDate)}</ViewField>
                    <ViewField label="Currency">{order.currency || '-'}</ViewField>
                    <ViewField label="Standalone">{standaloneLabel}</ViewField>
                    <ViewField label="GO Status">{order.goStatus || '-'}</ViewField>
                    <ViewField label="CPO Status">
                      <span className="fwt_600">
                        {capitalizeStatus(order.cpoStatus || order.status)}
                      </span>
                    </ViewField>
                    <ViewField label="Status">
                      <span
                        className={`badge ${String(order.status).toLowerCase() === 'active'
                            ? 'badge-success'
                            : 'badge-secondary'
                          }`}
                        style={{ fontSize: '90%', fontWeight: 550 }}
                      >
                        {capitalizeStatus(order.status)}
                      </span>
                    </ViewField>
                    <ViewField label="CPO Summary">
                      <span style={{ whiteSpace: 'pre-wrap' }}>{order.cpoSummary || '-'}</span>
                    </ViewField>

                    <SectionTitle icon="fa-user" title="Customer" />

                    <ViewField label="Customer Name">{order.customerName || '-'}</ViewField>
                    <ViewField label="Customer Mail ID">{order.customerMailId || '-'}</ViewField>
                    <ViewField label="Customer Number">
                      {order.customerNumber || order.phoneNumber || '-'}
                    </ViewField>

                    <SectionTitle icon="fa-list-check" title="Terms" />

                    {termsRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '160px' }}>Term</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {termsRows.map((term, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td
                                  style={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.term}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.response}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table
                          className="table table-bordered table-sm mb-0"
                          style={{ minWidth: '1100px' }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Ref No</th>
                              <th style={{ fontSize: '12px' }}>Material Code</th>
                              <th style={{ fontSize: '12px' }}>Material Description</th>
                              <th style={{ fontSize: '12px' }}>HSN</th>
                              <th style={{ fontSize: '12px' }}>UOM</th>
                              <th style={{ fontSize: '12px' }}>Quantity</th>
                              <th style={{ fontSize: '12px' }}>Basic Price</th>
                              <th style={{ fontSize: '12px' }}>GST %</th>
                              <th style={{ fontSize: '12px' }}>GST Value</th>
                              <th style={{ fontSize: '12px' }}>Total Value</th>
                              <th style={{ fontSize: '12px' }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.map((row, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{row.referenceNumber || '-'}</td>
                                <td>{row.materialCode || '-'}</td>
                                <td
                                  style={{
                                    maxWidth: '240px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.materialDescription || '-'}
                                </td>
                                <td>{row.hsn || row.hsnCode || '-'}</td>
                                <td>{row.uom || '-'}</td>
                                <td>{row.quantity ?? '-'}</td>
                                <td>{formatMaterialMoney(getMaterialBasicPrice(row))}</td>
                                <td>
                                  {getMaterialGstPercent(row) != null
                                    ? `${getMaterialGstPercent(row)}%`
                                    : '-'}
                                </td>
                                <td>{getMaterialGstValueDisplay(row)}</td>
                                <td>{getMaterialTotalValueDisplay(row)}</td>
                                <td
                                  style={{
                                    maxWidth: '200px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.technicalRemarks || '-'}
                                </td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f4f6f9' }}>
                              <td
                                colSpan={7}
                                className="text-right align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                Gross Total
                                {order.currency ? ` (${order.currency})` : ''}
                              </td>
                              <td
                                className="align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                {formatMaterialMoney(materialGrossTotals.grossTotalBasicPrice)}
                              </td>
                              <td />
                              <td
                                className="align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                {formatMaterialMoney(materialGrossTotals.grossGstValue)}
                              </td>
                              <td
                                className="align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                {formatMaterialMoney(materialGrossTotals.grossTotalValue)}
                              </td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-envelope" title="Resend Sales Order Email" />

                    <div className="row mb-3">
                      <div className="col-md-6 mb-3">
                        <label className="mb-1">To Email</label>
                        <div
                          className="form-control form-control-sm bg-light"
                          style={{ minHeight: '31px', height: 'auto' }}
                        >
                          {order.customerMailId || (
                            <span className="text-muted">No customer email</span>
                          )}
                        </div>
                        <small className="form-text text-muted">
                          Customer mail ID from the order (not editable).
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="mb-1">CC Emails (your email)</label>
                        <div
                          className="form-control form-control-sm bg-light"
                          style={{ minHeight: '31px', height: 'auto' }}
                        >
                          {userEmail || (
                            <span className="text-muted">
                              Profile email not available — add CC addresses below
                            </span>
                          )}
                        </div>
                        <small className="form-text text-muted">
                          Always included on send (not editable).
                        </small>
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="mb-1">Additional CC Emails</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Type name or email to search employees"
                            value={additionalCcEmails}
                            onChange={handleAdditionalCcChange}
                            onBlur={handleAdditionalCcBlur}
                            disabled={isSendingEmail}
                            autoComplete="off"
                          />
                          {(isSearchingCcUsers || ccUserSuggestions.length > 0) && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1060,
                                backgroundColor: '#fff',
                                border: '1px solid #d8e0ea',
                                borderRadius: '8px',
                                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                maxHeight: '210px',
                                overflowY: 'auto',
                                marginTop: '2px'
                              }}
                            >
                              {isSearchingCcUsers ? (
                                <div
                                  className="text-muted"
                                  style={{ padding: '10px 12px', fontSize: '13px' }}
                                >
                                  <i className="fa-solid fa-spinner fa-spin mr-2" />
                                  Searching employees...
                                </div>
                              ) : (
                                (() => {
                                  const existing = new Set(
                                    buildCcEmails(
                                      userEmail,
                                      additionalCcEmails,
                                      order.customerMailId
                                    ).map((item) => item.toLowerCase())
                                  )
                                  const visible = ccUserSuggestions.filter(
                                    (item) =>
                                      !existing.has(
                                        String(item.email).trim().toLowerCase()
                                      )
                                  )
                                  if (visible.length === 0) {
                                    return (
                                      <div
                                        className="text-muted"
                                        style={{
                                          padding: '10px 12px',
                                          fontSize: '13px'
                                        }}
                                      >
                                        No matching employees found
                                      </div>
                                    )
                                  }
                                  return visible.map((item) => (
                                    <button
                                      key={item.email}
                                      type="button"
                                      className="dropdown-item"
                                      style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '8px 12px',
                                        whiteSpace: 'normal',
                                        cursor: 'pointer'
                                      }}
                                      onMouseDown={(e) => {
                                        e.preventDefault()
                                        handleSelectCcUser(item.email)
                                      }}
                                    >
                                      <span
                                        className="d-block fwt_600"
                                        style={{ fontSize: '13px' }}
                                      >
                                        {item.name || item.email}
                                      </span>
                                      <small className="text-muted d-block">
                                        {item.email}
                                      </small>
                                    </button>
                                  ))
                                })()
                              )}
                            </div>
                          )}
                        </div>
                        <small className="form-text text-muted">
                          Type at least 3 characters to search internal employees.
                          Comma or semicolon separated.
                        </small>
                        {buildCcEmails(
                          userEmail,
                          additionalCcEmails,
                          order.customerMailId
                        ).length > 0 && (
                          <small className="form-text text-muted d-block">
                            Will CC:{' '}
                            {buildCcEmails(
                              userEmail,
                              additionalCcEmails,
                              order.customerMailId
                            ).join(', ')}
                          </small>
                        )}
                      </div>
                      <div className="col-md-12">
                        <button
                          type="button"
                          className="btn btn-blue btn-sm"
                          onClick={handleResendSalesOrderEmail}
                          disabled={
                            isSendingEmail ||
                            !String(order.customerMailId || '').trim() ||
                            !hasSalesOrderPdf(order) ||
                            String(order.status || '').toLowerCase() !== 'active'
                          }
                        >
                          {isSendingEmail ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-1" />
                              Resending...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-paper-plane mr-1" />
                              Resend Email
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {(hasSalesOrderPdf(order) || ownerAttachments.length > 0) && (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        {hasSalesOrderPdf(order) ? (
                          <ViewField label="PDF Link">
                            <button
                              type="button"
                              className="btn btn-link p-0 align-baseline"
                              onClick={handleViewPdf}
                              disabled={isOpeningPdf}
                            >
                              {isOpeningPdf ? (
                                <>
                                  <i className="fa-solid fa-spinner fa-spin mr-1" />
                                  Opening...
                                </>
                              ) : (
                                'View PDF'
                              )}
                            </button>
                          </ViewField>
                        ) : null}

                        {ownerAttachments.length > 0 ? (
                          <ViewField label="Owner Attachments">
                            <AttachmentTileGrid
                              attachments={ownerAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadOwnerAttachment}
                              disabled={Boolean(downloadingS3Key)}
                            />
                          </ViewField>
                        ) : null}
                      </>
                    )}

                    <SectionTitle icon="fa-clock" title="Audit" />

                    <ViewField label="Created By">{displayUser(order.createdBy)}</ViewField>
                    <ViewField label="Updated By">{displayUser(order.updatedBy)}</ViewField>
                    <ViewField label="Created At">{formatDateTime(order.createdAt)}</ViewField>
                    <ViewField label="Updated At">{formatDateTime(order.updatedAt)}</ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesOrderView